import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useBilling } from '../contexts/BillingContext';
import { useNotification } from '../contexts/NotificationContext';
import { useSidebar } from '../contexts/SidebarContext';
import { userService } from '../services/firestore';
import CheckoutModal from './CheckoutModal';
import { usePlanPricing } from '../hooks/usePlanPricing';
import { isRazorpayCheckoutSuppressed } from '../utils/paymentGatewayConfig';
import { createDodoCheckoutSession } from '../services/dodoPaymentService';
import { shouldSkipShopifyComplianceOnboarding } from '../utils/shopifyComplianceOnboarding';
import { buildContactTierOptions } from '../config/contactTierOptions';
import {
  PRO_MONTHLY_PRICING_TIERS,
  getProAnnualPrice,
  getProEffectiveMonthlyDisplayPrice,
  formatProPlanUsd,
} from '../config/proPlanPricing';

/**
 * After workspace onboarding, plan choice happens in-app (not /onboarding/plan).
 * Shown while profile.workspaceCompleted && !profile.planSelected.
 */
const PostSignupPlanModal: React.FC = () => {
  const { currentUser } = useAuth();
  const { updateSubscription, preferShopifyBilling, initiateShopifyBilling } = useBilling();
  const { showSuccess, showError } = useNotification();
  const { setIsSidebarVisible } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutRedirectPlan, setCheckoutRedirectPlan] = useState<'pro' | 'business' | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2>(1);

  type PlanTab = 'pro' | 'business' | 'starter';
  const [activePlanTab, setActivePlanTab] = useState<PlanTab>('pro');

  type CheckoutData = {
    plan: 'pro' | 'business';
    subscribers: number;
    currency: string;
    billingPeriod: 'monthly' | 'annual';
  };

  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedCheckoutData, setSelectedCheckoutData] = useState<CheckoutData | null>(null);

  const {
    proPricing,
    proAnnualPricing,
    businessPricing,
    starterMetadata,
    proMetadata,
    businessMetadata,
    isLoading: isLoadingPlanPricing,
  } = usePlanPricing();

  const [subscribers, setSubscribers] = useState<number>(1000);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('annual');
  const [isContactPickerOpen, setIsContactPickerOpen] = useState(false);

  const proTiersResolved = proPricing.length > 0 ? proPricing : PRO_MONTHLY_PRICING_TIERS;
  const contactRanges = useMemo(
    () => buildContactTierOptions(proTiersResolved),
    [proTiersResolved]
  );

  const DEFAULT_BUSINESS_TIERS = [
    { subscribers: 1000, price: 20 },
    { subscribers: 2500, price: 35 },
    { subscribers: 5000, price: 55 },
    { subscribers: 8000, price: 70 },
    { subscribers: 10000, price: 89 },
    { subscribers: 15000, price: 125 },
    { subscribers: 20000, price: 159 },
    { subscribers: 25000, price: 195 },
    { subscribers: 30000, price: 229 },
    { subscribers: 40000, price: 289 },
    { subscribers: 50000, price: 339 },
    { subscribers: 80000, price: 429 },
    { subscribers: 100000, price: 499 },
  ];

  const getTierMonthlyPrice = (tiers: Array<{ subscribers: number; price: number }>, subs: number) => {
    const tier = tiers.find(t => subs <= t.subscribers) || tiers[tiers.length - 1];
    return tier?.price ?? 0;
  };

  const shouldEvaluatePath = useCallback(() => {
    const p = location.pathname;
    if (p.startsWith('/onboarding')) return false;
    if (p.startsWith('/verify-email')) return false;
    if (p.startsWith('/shopify/onboarding')) return false;
    if (p === '/login' || p === '/signup' || p.startsWith('/signup/')) return false;
    if (p === '/forgot-password') return false;
    return true;
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!currentUser || !shouldEvaluatePath()) {
        setOpen(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const profile = await userService.getUserProfile(currentUser.uid);
        if (cancelled) return;
        const skipModalForShopifySignup = shouldSkipShopifyComplianceOnboarding(profile);
        const shopifyAppBilling =
          profile?.billingSource === 'shopify' ||
          profile?.acquisitionChannel === 'shopify_app_store';
        const needsPrompt = !!(
          profile?.workspaceCompleted &&
          !profile?.planSelected &&
          !skipModalForShopifySignup &&
          !shopifyAppBilling
        );
        setOpen(needsPrompt);
        if (needsPrompt) setStep(1);
      } catch (e) {
        console.error('PostSignupPlanModal: profile load failed', e);
        setOpen(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [currentUser, shouldEvaluatePath, location.pathname]);

  useEffect(() => {
    if (open && step === 2) {
      setActivePlanTab('pro'); // default tab
    }
  }, [open, step]);

  const handleContinueFree = async ({ closeModalFirst = false }: { closeModalFirst?: boolean } = {}) => {
    if (!currentUser) return;
    if (closeModalFirst) {
      // Close instantly so users don't see the async loading state.
      setOpen(false);
      // Ensure mobile dashboard lands with sidebar closed.
      if (window.innerWidth < 1024) {
        setIsSidebarVisible(false);
      }
    }
    setSubmitting(true);
    try {
      if (preferShopifyBilling) {
        await userService.updateUserProfile(currentUser.uid, {
          planSelected: true,
          planSelectedAt: Timestamp.now(),
        });
        showSuccess('You are on the Free plan. You can upgrade anytime from Billing.');
        setActivePlanTab('starter');
      } else {
        try {
          const { notifyRegistrationCompleteOnce } = await import(
            '../services/registrationNotificationService'
          );
          await notifyRegistrationCompleteOnce();
        } catch (notifyErr) {
          // Do not block free/starter activation if notification side-effect is deferred.
          console.warn('PostSignupPlanModal: notifyRegistrationCompleteOnce deferred/failed', notifyErr);
        }

        const starterSubscribers = Math.min(subscribers, 1000);
        try {
          await updateSubscription('starter', starterSubscribers, 'usd', billingPeriod);
        } catch (subscriptionErr) {
          // Keep skip/free onboarding unblocked; profile flag still marks starter selection intent.
          console.warn('PostSignupPlanModal: starter subscription update failed', subscriptionErr);
        }

        await userService.updateUserProfile(currentUser.uid, {
          planSelected: true,
          planSelectedAt: Timestamp.now(),
        });
        showSuccess('You are on the Free plan.');
        setActivePlanTab('starter');
      }
      setOpen(false);
      const { getDashboardRedirectPath } = await import('../utils/workspaceRedirect');
      navigate(await getDashboardRedirectPath(currentUser.uid), { replace: false });
    } catch (e) {
      console.error('PostSignupPlanModal: free plan failed', e);
      showError(e instanceof Error ? e.message : 'Could not activate the free plan. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectPaidPlan = async (plan: 'pro' | 'business') => {
    if (!currentUser) {
      showError('You must be signed in to continue.');
      return;
    }

    const subsForCheckout = subscribers === 100001 ? 100000 : subscribers;

    if (preferShopifyBilling) {
      setSubmitting(true);
      try {
        await initiateShopifyBilling(plan, subsForCheckout, billingPeriod);
      } catch (e) {
        console.error('PostSignupPlanModal: Shopify billing', e);
        showError(e instanceof Error ? e.message : 'Could not start Shopify billing.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (isRazorpayCheckoutSuppressed()) {
      setSubmitting(true);
      setCheckoutError(null);
      setCheckoutRedirectPlan(plan);
      try {
        let customerEmail = currentUser.email || '';
        let customerName = (currentUser.displayName && currentUser.displayName.trim()) || '';
        try {
          const profile = await userService.getUserProfile(currentUser.uid);
          if (profile) {
            if (profile.email) customerEmail = profile.email;
            const fromProfile = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
            if (fromProfile) customerName = fromProfile;
          }
        } catch {
          /* use auth fallbacks */
        }
        if (!customerName) {
          customerName = customerEmail.split('@')[0] || 'Customer';
        }
        const { checkoutUrl } = await createDodoCheckoutSession({
          plan,
          subscribers: subsForCheckout,
          billingPeriod,
          customerEmail,
          customerName,
          returnOrigin: typeof window !== 'undefined' ? window.location.origin : '',
        });
        void (async () => {
          try {
            const { notifyRegistrationCompleteOnce } = await import(
              '../services/registrationNotificationService'
            );
            await notifyRegistrationCompleteOnce();
            await userService.updateUserProfile(currentUser.uid, {
              planSelected: true,
              planSelectedAt: Timestamp.now(),
            });
          } catch (e) {
            console.warn('PostSignupPlanModal: background plan-selection side effects failed', e);
          }
        })();
        window.location.assign(checkoutUrl);
      } catch (e) {
        console.error('PostSignupPlanModal: Dodo checkout', e);
        const message = e instanceof Error ? e.message : 'Could not start checkout';
        setCheckoutError(message);
        showError(message);
      } finally {
        setSubmitting(false);
        setCheckoutRedirectPlan(null);
      }
      return;
    }

    setSelectedCheckoutData({
      plan,
      subscribers: subsForCheckout,
      currency: 'usd',
      billingPeriod,
    });
    setShowCheckoutModal(true);

    void (async () => {
      try {
        const { notifyRegistrationCompleteOnce } = await import(
          '../services/registrationNotificationService'
        );
        await notifyRegistrationCompleteOnce();

        await userService.updateUserProfile(currentUser.uid, {
          planSelected: true,
          planSelectedAt: Timestamp.now(),
        });
      } catch (e) {
        console.warn('PostSignupPlanModal: background plan-selection side effects failed', e);
      }
    })();
  };

  const formatUSD = (n: number) => formatProPlanUsd(n);

  const proTiers = proTiersResolved;
  const businessTiers = businessPricing.length > 0 ? businessPricing : DEFAULT_BUSINESS_TIERS;

  const effectiveSubscribers = subscribers === 100001 ? 100000 : subscribers;

  const proMonthlyPrice = getTierMonthlyPrice(proTiers, effectiveSubscribers);
  const proAnnualTotal = getProAnnualPrice(
    effectiveSubscribers,
    proAnnualPricing.length > 0 ? proAnnualPricing : undefined
  );
  const proDisplayMonthly = getProEffectiveMonthlyDisplayPrice(
    effectiveSubscribers,
    billingPeriod,
    proTiers,
    proAnnualPricing.length > 0 ? proAnnualPricing : undefined
  );
  const businessMonthlyPrice = getTierMonthlyPrice(businessTiers, effectiveSubscribers);

  const activeMetadata =
    activePlanTab === 'pro'
      ? proMetadata
      : activePlanTab === 'business'
        ? businessMetadata
        : starterMetadata;

  const businessAnnualTotal = businessMonthlyPrice * 12 * 0.9;
  const businessDisplayMonthly =
    billingPeriod === 'annual' ? businessAnnualTotal / 12 : businessMonthlyPrice;

  const starterMaxContacts =
    typeof starterMetadata?.limits.contacts === 'number' ? starterMetadata.limits.contacts : 1000;

  const isStarterDisabled = activePlanTab === 'starter' && subscribers > starterMaxContacts;

  const includedFeatures = (activeMetadata?.features || []).filter(f => f.included);

  const starterIncludedFeatures = (starterMetadata?.features || []).filter(f => f.included);
  const proIncludedFeatures = (proMetadata?.features || []).filter(f => f.included);
  const businessIncludedFeatures = (businessMetadata?.features || []).filter(f => f.included);

  const formatLimit = (v: number | 'unlimited' | undefined | null) => {
    if (v === 'unlimited') return 'Unlimited';
    if (typeof v === 'number') return v.toLocaleString();
    return '—';
  };

  const starterContactsLimit = formatLimit(starterMetadata?.limits.contacts);
  const starterEmailsLimit = formatLimit(starterMetadata?.limits.emailsPerMonth);
  const proContactsLimit = formatLimit(proMetadata?.limits.contacts);
  const proEmailsLimit = formatLimit(proMetadata?.limits.emailsPerMonth);
  const businessContactsLimit = formatLimit(businessMetadata?.limits.contacts);
  const businessEmailsLimit = formatLimit(businessMetadata?.limits.emailsPerMonth);
  const selectedContactRangeLabel =
    contactRanges.find((range) => range.value === subscribers)?.label ?? contactRanges[0].label;

  if ((!open && !showCheckoutModal) || loading) return null;

  return (
    <>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 md:p-8 bg-black/50 backdrop-blur-sm">
        <div
          className={`w-full shadow-2xl border relative overflow-visible max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-3rem)] md:max-h-[calc(100vh-4rem)] overflow-y-auto ${
            step === 1
              ? 'max-w-5xl rounded-2xl bg-[#0b0d17] border-white/10'
              : 'max-w-5xl rounded-2xl bg-[#0b0d17] border-white/10'
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="post-signup-plan-title"
        >
        {step === 1 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 min-h-[560px]">
            <div className="p-6 sm:p-8 md:p-12 flex flex-col justify-center bg-[#090b14] text-white">
              <h2 id="post-signup-plan-title" className="text-3xl sm:text-4xl font-semibold leading-tight">
                Welcome to Scubamail
              </h2>
              <p className="text-slate-300 text-base sm:text-lg mt-3">
                Generate and send email content without complexity.
              </p>
              <div className="mt-8 sm:mt-10 space-y-3 max-w-md text-sm text-slate-300 leading-relaxed">
                <p>
                  You are one step away from completing your setup. In the next step, choose the plan that
                  matches your current stage.
                </p>
                <p>
                  You can begin with Free and upgrade anytime as your list and sending volume grow.
                </p>
                <p className="text-slate-400">
                  Your account remains fully accessible after this step, and you can manage plan details from
                  Billing at any time.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="mt-8 sm:mt-10 w-full sm:w-40 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 transition-colors text-white font-semibold"
              >
                Continue
              </button>
            </div>
            <div className="relative overflow-hidden bg-gradient-to-br from-rose-200 via-fuchsia-100 to-orange-100 hidden md:flex items-center justify-center p-6 md:p-8">
              <div className="relative z-10 w-full h-full rounded-2xl bg-white/35 p-3 md:p-4 border border-white/45">
                <img
                  src="/images/Scubamail-Onboarding.png"
                  alt="Scubamail onboarding illustration"
                  className="w-full h-full max-h-[500px] object-contain drop-shadow-[0_18px_32px_rgba(15,23,42,0.16)]"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 min-h-[560px] bg-[#090b14] text-white">
            <div className="md:col-span-2 p-4 sm:p-6 md:p-10 lg:p-12 flex flex-col">
              {/* Single-screen pricing UI: contacts selector + 3 plan cards */}
              <div className="flex justify-center">
                <div className="max-w-md w-full">
                  <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2 text-center">
                    Contacts
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsContactPickerOpen(true)}
                    disabled={showCheckoutModal}
                    className="md:hidden w-full bg-neutral-800/50 text-white px-3 py-2.5 rounded-lg border border-white/10 hover:border-blue-400/40 focus:border-blue-500/60 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 text-base font-medium text-center shadow-sm flex items-center justify-between"
                    aria-label="Choose contacts range"
                    aria-haspopup="dialog"
                    aria-expanded={isContactPickerOpen}
                  >
                    <span className="truncate">{selectedContactRangeLabel}</span>
                    <svg className="w-4 h-4 ml-2 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="relative z-30 hidden md:block">
                    <select
                      value={subscribers}
                      onChange={(e) => {
                        setSubscribers(parseInt(e.target.value, 10) || 1000);
                        setCheckoutError(null);
                      }}
                      disabled={showCheckoutModal}
                      className="w-full bg-neutral-800/50 text-white px-3 py-2.5 rounded-lg border border-white/10 hover:border-blue-400/40 focus:border-blue-500/60 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 cursor-pointer appearance-none text-base md:text-sm font-medium text-center shadow-sm"
                      aria-label="Select contacts"
                      style={{
                        backgroundImage: `url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        backgroundSize: '16px',
                        paddingRight: '36px',
                      }}
                    >
                      {contactRanges.map((range) => (
                        <option key={range.value} value={range.value} className="bg-neutral-800 text-white text-base">
                          {range.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {isContactPickerOpen && (
                <div className="fixed inset-0 z-[130] bg-[#090b14] text-white md:hidden flex flex-col">
                  <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
                    <h3 className="text-lg font-semibold">Select contacts</h3>
                    <button
                      type="button"
                      onClick={() => setIsContactPickerOpen(false)}
                      className="px-3 py-1.5 rounded-lg border border-white/15 text-sm text-white/90 hover:bg-white/10"
                    >
                      Done
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto px-3 py-3">
                    <div className="space-y-2">
                      {contactRanges.map((range) => {
                        const isActive = subscribers === range.value;
                        return (
                          <button
                            key={range.value}
                            type="button"
                            onClick={() => {
                              setSubscribers(range.value);
                              setIsContactPickerOpen(false);
                            }}
                            className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                              isActive
                                ? 'border-blue-500 bg-blue-500/20 text-white'
                                : 'border-white/10 bg-neutral-800/40 text-gray-200 hover:border-white/20'
                            }`}
                          >
                            <span className="text-base font-medium">{range.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {checkoutError && (
                <div
                  className="mt-4 rounded-xl border border-red-500/40 bg-red-950/40 px-4 py-3 text-sm text-red-200"
                  role="alert"
                >
                  {checkoutError}
                </div>
              )}

              <div className="mt-5 sm:mt-6 flex md:grid md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 overflow-x-auto md:overflow-visible pb-2 md:pb-0 -mx-1 px-1 md:mx-0 md:px-0 snap-x snap-mandatory md:snap-none">
                {/* Starter Plan */}
                <div className="w-[86%] shrink-0 snap-center md:w-auto md:shrink">
                  {(() => {
                      const starterBorder =
                        activePlanTab === 'starter' ? 'border-white/30' : 'border-white/15';
                    const disabled = subscribers > starterMaxContacts;
                    return (
                      <div
                          className={`relative px-5 sm:px-6 pt-5 sm:pt-6 pb-5 sm:pb-6 h-full bg-neutral-800 border ${starterBorder} overflow-hidden rounded-2xl hover:border-white/30 transition-all duration-200 flex flex-col`}
                      >
                        <p className="mb-2 text-base text-white font-light">Starter</p>
                        {/* Minimal plan copy: contacts + emails only */}
                        <p className="mb-3 text-white font-medium text-2xl sm:text-3xl">
                          $0<span className="font-medium text-gray-400 text-sm">/ month</span>
                        </p>

                        <div className="mt-2 mb-1 text-sm text-gray-300/90 leading-relaxed">
                          Up to {starterContactsLimit} contacts • {starterEmailsLimit} emails/month
                        </div>

                        <div className="mt-auto pt-3">
                          <button
                            onClick={() => {
                              setActivePlanTab('starter');
                              handleContinueFree();
                            }}
                            disabled={disabled || submitting || showCheckoutModal}
                            className={`relative z-10 block w-full px-6 py-2.5 text-center font-medium tracking-wide border-2 ${
                              disabled
                                ? 'border-gray-500 bg-neutral-700 text-gray-500 cursor-not-allowed'
                                : activePlanTab === 'starter'
                                  ? 'border-blue-500 bg-blue-500 text-white'
                                  : 'border-blue-500 bg-blue-500 hover:bg-blue-600 text-white'
                            } focus:ring-4 focus:ring-blue-500 focus:ring-opacity-40 rounded-full transition duration-300 cursor-pointer`}
                          >
                            {disabled ? 'Not Available' : activePlanTab === 'starter' ? 'Selected' : 'Get Started Free'}
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Pro Plan */}
                <div className="w-[86%] shrink-0 snap-center md:w-auto md:shrink">
                  {(() => {
                    const proYearlySavingsPct = Math.max(
                      0,
                      Math.round((1 - proAnnualTotal / (proMonthlyPrice * 12)) * 100)
                    );
                    const disabled = showCheckoutModal;
                    return (
                      <div
                        className={`relative px-5 sm:px-6 pt-5 sm:pt-6 pb-5 sm:pb-6 h-full bg-neutral-800 border overflow-hidden rounded-2xl transition-all duration-200 flex flex-col ${
                          activePlanTab === 'pro' ? 'border-white/30' : 'border-white/15'
                        }`}
                      >
                        <div className="absolute top-3 right-3 bg-blue-500 text-white px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium">
                          Most Popular
                        </div>
                        <p className="mb-2 text-base text-white font-light">Pro</p>
                        <div className="mb-3 text-sm text-gray-300/90 leading-relaxed">
                          Up to {proContactsLimit} contacts • {proEmailsLimit} emails/month
                        </div>
                        <p className="mb-3 text-white font-medium text-2xl sm:text-3xl">
                          {formatUSD(proDisplayMonthly)}
                          <span className="font-medium text-gray-400 text-sm">/ month</span>
                        </p>

                        {billingPeriod === 'annual' && (
                          <p className="mb-3 text-sm text-gray-400">
                            {formatUSD(proAnnualTotal)} billed yearly for up to {effectiveSubscribers.toLocaleString()} subscribers
                          </p>
                        )}

                        {/* Billing period selection (matches UpgradeModal) */}
                        <div className="mb-3">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={billingPeriod === 'annual'}
                              onChange={(e) => setBillingPeriod(e.target.checked ? 'annual' : 'monthly')}
                              className="sr-only"
                            />
                            <div
                              className={`flex items-center justify-center w-5 h-5 mr-3 rounded border-2 transition-all ${
                                billingPeriod === 'annual'
                                  ? 'border-blue-500 bg-blue-500'
                                  : 'border-gray-400 bg-transparent'
                              }`}
                            >
                              {billingPeriod === 'annual' && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-300">Bill annually</span>
                              {billingPeriod === 'annual' && proYearlySavingsPct > 0 && (
                                <span className="px-2 py-0.5 text-xs font-medium text-green-400 uppercase border border-green-400 rounded">
                                  Save {proYearlySavingsPct}%
                                </span>
                              )}
                            </div>
                          </label>
                        </div>

                        {/* Minimal plan copy handled above (contacts + emails only) */}

                        <div className="mt-auto pt-3">
                          <button
                            onClick={() => {
                              handleSelectPaidPlan('pro');
                            }}
                            disabled={submitting || showCheckoutModal}
                            className="relative z-10 block w-full px-6 py-2.5 text-center font-medium tracking-wide border-2 border-blue-500 bg-blue-500 hover:bg-blue-600 text-white focus:ring-4 focus:ring-blue-500 focus:ring-opacity-40 rounded-full transition duration-300 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {checkoutRedirectPlan === 'pro'
                              ? 'Opening checkout…'
                              : 'Select Pro Plan'}
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Business Plan */}
                <div className="w-[86%] shrink-0 snap-center md:w-auto md:shrink">
                  {(() => {
                    const businessYearlySavingsPct = Math.max(
                      0,
                      Math.round((1 - businessAnnualTotal / (businessMonthlyPrice * 12)) * 100)
                    );
                    return (
                      <div
                        className={`relative px-5 sm:px-6 pt-5 sm:pt-6 pb-5 sm:pb-6 h-full bg-neutral-800 border overflow-hidden rounded-2xl transition-all duration-200 flex flex-col ${
                          activePlanTab === 'business' ? 'border-white/30' : 'border-white/15'
                        }`}
                      >
                        <p className="mb-2 text-base text-white font-light">Business</p>
                        <div className="mb-3 text-sm text-gray-300/90 leading-relaxed">
                          Unlimited contacts • Unlimited email sends
                        </div>
                        <p className="mb-3 text-white font-medium text-2xl sm:text-3xl">
                          {formatUSD(businessDisplayMonthly)}
                          <span className="font-medium text-gray-400 text-sm">/ month</span>
                        </p>

                        {billingPeriod === 'annual' && (
                          <p className="mb-3 text-sm text-gray-400">
                            {formatUSD(businessAnnualTotal)} billed yearly for up to {effectiveSubscribers.toLocaleString()} subscribers
                          </p>
                        )}

                        <div className="mb-3">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={billingPeriod === 'annual'}
                              onChange={(e) => setBillingPeriod(e.target.checked ? 'annual' : 'monthly')}
                              className="sr-only"
                            />
                            <div
                              className={`flex items-center justify-center w-5 h-5 mr-3 rounded border-2 transition-all ${
                                billingPeriod === 'annual'
                                  ? 'border-purple-500 bg-purple-500'
                                  : 'border-gray-400 bg-transparent'
                              }`}
                            >
                              {billingPeriod === 'annual' && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-300">Bill annually</span>
                              {billingPeriod === 'annual' && businessYearlySavingsPct > 0 && (
                                <span className="px-2 py-0.5 text-xs font-medium text-green-400 uppercase border border-green-400 rounded">
                                  Save {businessYearlySavingsPct}%
                                </span>
                              )}
                            </div>
                          </label>
                        </div>

                        {/* Whats included intentionally removed for this onboarding flow */}

                        <div className="mt-auto pt-3">
                          <button
                            onClick={() => {
                              handleSelectPaidPlan('business');
                            }}
                            disabled={submitting || showCheckoutModal}
                            className="relative z-10 block w-full px-6 py-2.5 text-center font-medium tracking-wide border-2 border-purple-500 bg-purple-500 hover:bg-purple-600 text-white focus:ring-4 focus:ring-purple-500 focus:ring-opacity-40 rounded-full transition duration-300 cursor-pointer"
                          >
                            Start Business Plan
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Bottom controls: simple back arrow (left) + skip link (right) */}
              <div className="mt-6 sm:mt-8 pt-2 flex items-center justify-between">
                <button
                  type="button"
                  disabled={submitting || showCheckoutModal}
                  onClick={() => setStep(1)}
                  className="flex items-center text-sm text-white/70 hover:text-white transition-colors disabled:opacity-50"
                  aria-label="Back"
                >
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>

                <button
                  type="button"
                  disabled={submitting || showCheckoutModal}
                  onClick={() => {
                    setActivePlanTab('starter');
                    void handleContinueFree({ closeModalFirst: true });
                  }}
                  className="text-sm text-white/70 hover:text-white underline underline-offset-4 transition-colors disabled:opacity-50"
                >
                  Skip for now
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Paid plan pricing / checkout */}
      <CheckoutModal
        isOpen={showCheckoutModal}
        onClose={() => {
          setShowCheckoutModal(false);
          setSelectedCheckoutData(null);
        }}
        checkoutData={selectedCheckoutData}
        onSuccess={async () => {
          try {
            setShowCheckoutModal(false);
            setSelectedCheckoutData(null);
            setOpen(false);
            if (currentUser) {
              const { getDashboardRedirectPath } = await import('../utils/workspaceRedirect');
              navigate(await getDashboardRedirectPath(currentUser.uid), { replace: false });
            }
          } catch (e) {
            console.error('PostSignupPlanModal: post-checkout success navigation failed', e);
          }
        }}
      />
    </>
  );
};

export default PostSignupPlanModal;
